import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { translateLocation } from '../../utils/translationUtils';
import UserAvatar from '../../components/UserAvatar';
import './AdminLayout.css';

const AdminLayout = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { data } = useData();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState((user.location || 'all').toLowerCase());

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleTitle = () => {
    switch (user.role) {
      case 'admin':
        return t('roles.admin');
      case 'manager':
        return t('roles.manager');
      case 'instructor':
        return t('roles.instructor');
      default:
        return t('roles.student');
    }
  };

  const adminLinks = [{ path: '/admin', label: t('admin.dashboard'), exact: true }];

  if (user.role === 'admin' || user.role === 'manager') {
    adminLinks.push({ path: '/admin/studies', label: t('admin.viewHierarchy') });
    adminLinks.push({ path: '/admin/study-registrations', label: t('admin.studyRegistrations') });
    adminLinks.push({ path: '/admin/course-assignments', label: t('admin.courseAssignments') });
    adminLinks.push({ path: '/admin/payments', label: t('admin.payments') });
    adminLinks.push({ path: '/admin/users', label: t('admin.users') });
    adminLinks.push({ path: '/admin/cohorts', label: t('admin.cohorts') });
    adminLinks.push({ path: '/admin/departments', label: t('admin.departments') });
    adminLinks.push({ path: '/admin/content', label: t('admin.websiteContent') });
    adminLinks.push({ path: '/admin/notifications', label: t('admin.notifications') });
    adminLinks.push({ path: '/admin/declarations', label: t('admin.declarations') });
  }

  if (user.role === 'instructor' || user.role === 'manager') {
    adminLinks.push({ path: '/admin/homework', label: t('nav.homework') });
    adminLinks.push({ path: '/admin/module-materials', label: t('admin.moduleMaterials') });
  }

  if (user.role === 'admin') {
    adminLinks.push({ path: '/admin/module-materials', label: t('admin.moduleMaterials') });
  }

  adminLinks.push({ path: '/admin/calendar', label: t('admin.calendar') });
  adminLinks.push({ path: '/admin/report-payments', label: t('report.paymentReport', 'Betalingen Rapport') });
  adminLinks.push({ path: '/admin/report-grades', label: t('report.gradesReport', 'Cijfers Rapport') });

  const locations = [
    { id: 'all', name: t('admin.allLocations') },
    ...(data?.locations || [])
  ];
  const displayName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <UserAvatar user={user} size="lg" className="sidebar-avatar" />
          <h2>{t('admin.title')}</h2>
          <p className="user-role">{getRoleTitle()}</p>
          <p className="user-name">{displayName}</p>
          {user.location && <p className="user-location-chip">{translateLocation(t, String(user.location).toLowerCase())}</p>}
        </div>

        {/* Location Filter */}
        {user.role === 'admin' && (
          <div className="location-filter">
            <label>{t('admin.location')}</label>
            <select 
              value={selectedLocation} 
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.id === 'all' ? loc.name : translateLocation(t, loc.id)}
                </option>
              ))}
            </select>
          </div>
        )}

        <nav className="sidebar-nav">
          {adminLinks.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.exact}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-logout-btn" onClick={handleLogout}>
            {t('nav.logout')}
          </button>
          <p>NLBA LMS</p>
        </div>
      </aside>

      <main className="admin-main">
        <Outlet context={{ selectedLocation }} />
      </main>
    </div>
  );
};

export default AdminLayout;
